import React from 'react';
import { Link, Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';
import SEO from '../../shared/components/SEO';

// Stable anchor id from a section title, so every clause is linkable and a
// reviewer can cite "re-stack.com/privacy#retention" rather than "section 6".
const slugify = (title) =>
  title
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const sections = [
  {
    title: 'Overview',
    paragraphs: [
      'Restack operates the website at re-stack.com and Restack add-ins for Microsoft PowerPoint, Excel, and Word. This Privacy Policy explains how we collect, use, disclose, and protect information when you use our website, applications, integrations, and related services.',
      'By using Restack, you acknowledge that your prompts, Office content, uploaded files, and generated outputs may be processed by Restack and its service providers as needed to provide the service.',
    ],
  },
  {
    title: 'Information We Collect',
    paragraphs: [
      'We collect information that you provide directly, information generated through your use of the service, and certain technical information needed to operate, secure, and improve Restack.',
    ],
    bullets: [
      'Account and identity data, such as your name, email address, authentication-provider details, profile image, and authentication identifiers.',
      'Workspace and content data, such as prompts, Office content and metadata, uploaded files, generated outputs, comments, and settings.',
      'Billing and subscription information, such as plan type, payment status, invoices, and subscription metadata. Payment card data is processed by Stripe and is not stored in full by Restack.',
      'Usage and device information, such as IP address, browser type, operating system, error logs, performance metrics, diagnostics, and analytics events.',
      'Support and communications data, such as messages, feedback, support requests, and related attachments.',
    ],
  },
  {
    title: 'How We Use Information',
    bullets: [
      'Provide, operate, and maintain the Restack website and Microsoft Office add-ins.',
      'Authenticate users, manage accounts, and connect the add-in to third-party identity providers.',
      'Process prompts and Office content so the service can analyze, create, edit, and return outputs in PowerPoint, Excel, and Word.',
      'Deliver billing, subscription management, receipts, account notifications, and customer support.',
      'Monitor reliability, detect abuse, investigate incidents, enforce our terms, and improve security.',
      'Measure usage, improve product performance, and develop new features using aggregated or de-identified analytics where appropriate.',
    ],
  },
  {
    title: 'How We Share Information',
    paragraphs: [
      'We share information only as reasonably necessary to operate the service, comply with law, protect rights and security, or complete a business transaction.',
    ],
    bullets: [
      'Infrastructure and hosting providers, including Google Cloud, Firebase, and related platform services.',
      'Authentication and identity providers, such as Microsoft, when you choose to sign in using those services.',
      'Payment processors and billing vendors, such as Stripe, to manage subscriptions and payments.',
      'AI and model providers, such as Anthropic, when needed to process prompts, analyze Office content, and generate outputs.',
      'Analytics, diagnostics, customer support, and communications providers used to operate and improve the service.',
      'Government authorities, regulators, courts, or counterparties when required by law or when necessary to establish, exercise, or defend legal claims.',
      'A successor entity in connection with a merger, acquisition, financing, asset sale, or similar corporate transaction.',
    ],
  },
  {
    title: 'Your Content and AI Processing',
    paragraphs: [
      'When you use Restack, you may submit prompts, slides, workbooks, documents, images, and other materials. Restack processes that content to provide the requested functionality, including answering questions, generating drafts, editing Office content, and producing outputs inside PowerPoint, Excel, and Word.',
      'Because Restack is an AI-assisted product, generated outputs may be incomplete, inaccurate, or unsuitable for a particular purpose. You are responsible for reviewing outputs before using or sharing them.',
    ],
  },
  {
    title: 'Retention',
    paragraphs: [
      'We retain information for as long as reasonably necessary to provide the service, comply with legal obligations, resolve disputes, enforce agreements, and maintain security and business records. Retention periods may vary depending on the type of data, your subscription status, and product settings.',
    ],
  },
  {
    title: 'Security',
    paragraphs: [
      'We use administrative, technical, and organizational safeguards designed to protect information against unauthorized access, loss, misuse, or alteration. No system is perfectly secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    title: 'International Data Transfers',
    paragraphs: [
      'Restack and its service providers may process information in countries other than your own. By using the service, you understand that information may be transferred to and processed in jurisdictions that may have different data protection rules than the jurisdiction in which you are located.',
    ],
  },
  {
    title: 'Your Choices and Rights',
    bullets: [
      'You may update certain account details through your account or by contacting us.',
      'You may cancel paid subscriptions through the applicable billing flow.',
      'You may request deletion of your account or personal information, subject to legal, contractual, and security-related retention requirements.',
      'You may opt out of certain non-essential communications by using the unsubscribe instructions in those messages.',
    ],
  },
  {
    title: 'Children\u2019s Privacy',
    paragraphs: [
      'Restack is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided personal information to us, contact us so we can investigate and take appropriate action.',
    ],
  },
  {
    title: 'Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. When we make material changes, we may revise the effective date above and take additional steps to notify you when appropriate. Your continued use of the service after an update becomes effective means you accept the revised policy.',
    ],
  },
];

const CONTACT_TITLE = 'Contact Us';

// The on-this-page nav, built from the same list the document renders, so a
// section can never appear in one and not the other.
const SECTION_LINKS = [...sections.map((s) => s.title), CONTACT_TITLE].map((title) => ({
  id: slugify(title),
  label: title,
}));

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      eyebrow="Privacy Policy"
      title="How Restack handles your data"
      subtitle="This policy explains what information Restack collects, how it is used, when it is shared, and what choices you have when using the Restack website and Microsoft Office add-ins."
      effectiveDate="March 16, 2026"
      sectionLinks={SECTION_LINKS}
    >
      <SEO
        title="Privacy Policy"
        description="How Restack collects, uses, and protects your data, including prompts, Office content, and uploaded files."
        canonical="/privacy"
      />
      {sections.map((section) => (
        <React.Fragment key={section.title}>
          <Typography component="h2" id={slugify(section.title)}>{section.title}</Typography>
          {section.paragraphs?.map((paragraph, pIdx) => (
            <Typography key={pIdx}>{paragraph}</Typography>
          ))}
          {section.bullets?.length ? (
            <ul>
              {section.bullets.map((bullet, bIdx) => (
                <li key={bIdx}>{bullet}</li>
              ))}
            </ul>
          ) : null}
        </React.Fragment>
      ))}

      <Typography component="h2" id={slugify(CONTACT_TITLE)}>{CONTACT_TITLE}</Typography>
      <Typography>
        If you have questions about this Privacy Policy or want to make a
        privacy-related request, email{' '}
        <Link href="mailto:hi@re-stack.com">hi@re-stack.com</Link>.
      </Typography>
    </LegalPageLayout>
  );
}
